/**
 * The auth screens' captcha waits for the player (2026-10-15, QA 10.10 #2).
 *
 * The template's auth screens (`core/auth/*`, which this repo must not edit)
 * render `<altcha-widget auto="onload">`: the proof-of-work runs the moment
 * the card appears, so the "Verified" box is already ticked before the player
 * has touched anything — QA read it as a pre-checked consent box.
 *
 * This flips the widget to `auto="off"` from the game side, right after the
 * core screen renders. ALTCHA's custom element defers its mount by a
 * microtask and re-reads `auto` reactively, so a synchronous attribute change
 * lands before the 1ms "onload" timer is ever scheduled; the box then starts
 * empty and ticks only when the player clicks it.
 *
 * With nothing auto-solving, a submit before the tick would hit the core
 * handler's "Please wait for verification to complete." — wrong advice when
 * nothing is running. A capture-phase submit listener answers that case
 * first with what to actually do. While a solve is in flight the core
 * message is the right one, so it is left alone.
 */

/** Minimal shape of the ALTCHA element's public API used here. */
interface AltchaWidget extends HTMLElement {
  getState?: () => string;
}

import { N_, t } from '../i18n/i18n';

/** English; shown through `t()`. */
export const TICK_PROMPT = N_('Tick the verification box first.');

/**
 * Applies to every `<altcha-widget>` under `root`. Returns the teardown that
 * removes the submit guards (the widgets go with the core screen's markup).
 */
export function requireManualAltcha(root: HTMLElement): () => void {
  const offs: Array<() => void> = [];
  root.querySelectorAll<AltchaWidget>('altcha-widget').forEach((widget) => {
    widget.setAttribute('auto', 'off');
    const form = widget.closest('form');
    if (!form) return;
    const guard = (e: Event) => {
      const payload = new FormData(form).get('altcha');
      if (payload) return;
      if (widget.getState?.() === 'verifying') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const err = form.querySelector<HTMLElement>('[id$="-error"]');
      if (err) {
        err.textContent = t(TICK_PROMPT);
        err.hidden = false;
      }
      widget.querySelector<HTMLInputElement>('input[type="checkbox"]')?.focus();
    };
    form.addEventListener('submit', guard, { capture: true });
    offs.push(() => form.removeEventListener('submit', guard, { capture: true }));
  });
  return () => offs.forEach((off) => off());
}
